import {Domain} from './domain';
import {PaymentMethod} from './payment';
import {DocumentType} from '@gatewee/types/document';
import {PixKeyType} from './banking-info';
import {
  PaymentProvider,
  PaymentProviderCapabilities,
  PaymentProviderSettings,
} from './provider';
export interface DomainSetting {
  id: number;
  domain: Domain;
  key: string;
  value: string | object;
  createdAt: Date;
  updatedAt: Date;
  default?: boolean;
}

export interface DomainStyleSettings {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logo: string;
  logoWhite: string;
  logoBlack: string;
  logoSymbol: string;
  favicon: string;
  theme: 'dark' | 'light';
  customCss?: string;
}

export interface DomainPageSettings {
  title: string;
  description: string;
  keywords: string;
  googleAnalyticsId: string;
  supportContactEmail: string;
  supportContactWhatsapp: string;
  institutionalWebsiteURL: string;
  footer?: {
    copyright?: string;
  };

  assets?: {
    termsOfService?: string;
    privacyPolicy?: string;
  };

  mobile: {
    hero: {
      title: string;
      description: string;
      image: string;
    };
  };

  onboarding: {
    title: string;
    description: string;
    sidePanel: {
      backgroundImage: string;
      headerTitle: string;
      footerTitle: string;
      footerDescription: string;
    };
  };

  checkout: {
    footer?: {
      disclaimer?: string;
      copyright?: string;
    };
  };

  login?: {
    sidePanel?: {
      backgroundImage?: string;
      /** Raw HTML — highlight spans etc.; bind with [innerHTML] + DomSanitizer */
      headline?: string;
      subheadline?: string;
      stats?: Array<{value: string; label: string}>;
    };
    mobileApp?: {
      appStoreUrl?: string;
      googlePlayUrl?: string;
    };
  };
}

export interface DomainMailerSettings {
  mailFromAddress: string;
  mailFromName: string;
}

export interface DomainBankingInfo {
  pixKey: string;
  pixKeyType: PixKeyType;
  name: string;
  document: string;
}

export interface GateweeCollectionConfig {
  bankingInfo: DomainBankingInfo;
  batchMinimumAmount: number;
}

/**
 * Client-side tracking: IDs sent to the frontend for browser-based tracking (pixels, tags).
 * Safe to expose in API responses.
 */
export interface DomainTrackingClientConfig {
  /** GA4 measurement ID (e.g. G-XXXXXXXX). */
  ga4MeasurementId?: string;
  /** Meta Pixel ID. */
  metaPixelId?: string;
  /** TikTok Pixel ID. */
  tiktokPixelId?: string;
}

/**
 * Server-side tracking: secrets/tokens for server-side tracking APIs.
 * Must never be sent to the frontend; use only in backend code.
 */
export interface DomainTrackingServerConfig {
  ga4ApiSecret?: string;
  metaConversionsApiToken?: string;
  tiktokEventsApiToken?: string;
}

/**
 * Domain tracking configuration: client-side IDs and optional server-side secrets.
 * When exposing to the frontend, pass only the `client` portion.
 */
export interface DomainTrackingConfig {
  /** Client-side: IDs for frontend events. Safe to include in API responses. */
  client?: DomainTrackingClientConfig;
  /** Server-side: secrets/tokens for server-side tracking. Never send to the frontend. */
  server?: DomainTrackingServerConfig;
}

export enum DomainSettingKey {
  BANKING_INFO = 'bankingInfo',
  PAYMENT_METHODS = 'paymentMethods',
  FEES = 'fees',
  ALLOW_NATURAL_PERSON_SIGNUP = 'allowNaturalPersonSignup',
  ALLOW_LEGAL_PERSON_SIGNUP = 'allowLegalPersonSignup',
  MAILER = 'mailer',
  STYLE = 'style',
  PAGE = 'page',
  FQDN = 'fqdn',
  COMPLIANCE_REQUIREMENTS = 'complianceRequirements',
  STANDALONE_OPERATION = 'standaloneOperation',
  GATEWEE_COLLECTION_CONFIG = 'gateweeCollectionConfig',
  FEE_BATCH_MINIMUM_AMOUNT = 'feeBatchMinimumAmount',

  TRACKING = 'tracking',

  PROVIDERS_BASE_FEES = 'providersBaseFees',
  ENABLED_COMMUNICATIONS = 'enabledCommunications',
  FIRE_ONBOARDING = 'fireOnboarding',
  PAYTIME_ONBOARDING = 'paytimeOnboarding',

  PROVIDER_FIRE = 'provider:fire',

  RETENTION_DEFINITION = 'retentionDefinition',

  ENABLED_PROVIDERS = 'enabledProviders',

  DEFAULT_PROVIDER = 'defaultProvider',
  ENABLE_PAYMENT_SPLIT = 'enablePaymentSplit',

  DEFAULT_RECIPIENT_SETTINGS = 'defaultRecipientSettings',

  ANTIFRAUD = 'antifraud',

  ENABLE_DOMAIN_WHATSAPP = 'enableDomainWhatsapp',

  PROVIDER_SETTINGS_OVERRIDES = 'providerSettingsOverrides',

  /** Overrides for provider capabilities per provider. Merged with base capabilities from introspection. */
  PROVIDER_CAPABILITIES_OVERRIDE = 'providerCapabilitiesOverride',

  /** Mobile app build manifest for CI (Expo, native identifiers, assets). */
  MOBILE_BUILD_MANIFEST = 'mobileBuildManifest',

  /** When true, automatic fee batch creation and processing run for the domain/recipient; when false, only manual processing via endpoints. Same key as recipient for inheritance. */
  ENABLE_AUTO_FEE_COLLECTION = 'enableAutoFeeCollection',

  /** When true, Fee Plan V2 (assignable fee plans) is enabled for the domain. */
  FEE_PLAN_V2 = 'feePlanV2',

  REQUIRE_BANK_ACCOUNT_APPROVAL = 'requireBankAccountApproval',

  /** External onboarding API configuration (URL/credentials). */
  ONBOARDING_API = 'onboardingApi',

  /** Onboarding flow settings (JSON object). */
  ONBOARDING_SETTINGS = 'onboardingSettings',

  ENABLE_MASTER_PROVIDER_FEE_DEDUCTION = 'enableMasterProviderFeeDeduction',

  /** When true, PIX invoices get a public checkout URL (paymentData.publicUrl). Opt-in, default off. */
  PIX_CHECKOUT_URL_ENABLED = 'pixCheckoutUrlEnabled',

  /**
   * Allowlist of PIX key kinds for free PIX payouts with inline bankAccount.
   * Same key as recipient for inheritance (AND-merge). Unset = all kinds allowed.
   */
  ALLOWED_WITHDRAWAL_PIX_KEY_TYPES = 'allowedWithdrawalPixKeyTypes',

  /** When true, recipients can pay boletos (bank-slip bill payment / cash-out). */
  ENABLE_BANK_SLIP_PAYMENT = 'enableBankSlipPayment',
}

/** Android-specific build config for the mobile app. */
export interface MobileBuildManifestAndroid {
  package: string;
  adaptiveIcon?: {
    backgroundColor?: string;
    foregroundImageUrl?: string;
    backgroundImageUrl?: string;
    monochromeImageUrl?: string;
  };
}

/** iOS-specific build config for the mobile app. */
export interface MobileBuildManifestIos {
  bundleIdentifier: string;
}

/** Splash screen config for the mobile app. */
export interface MobileBuildManifestSplash {
  backgroundColor?: string;
}

/**
 * Mobile build manifest returned to CI for building the app.
 * See GET /domains-settings/public/mobile/build-manifest.
 */
export interface MobileBuildManifest {
  /** Display name (Play/App Store and on device). */
  appName: string;
  /** Expo slug (e.g. client-app-name). */
  slug: string;
  /** URL scheme (e.g. clientapp). */
  scheme: string;
  android: MobileBuildManifestAndroid;
  ios: MobileBuildManifestIos;
  /** Backend API base URL for this client. */
  apiUrl: string;
  /** Domain for cookies/links (e.g. app.client.com). */
  domainFqdn: string;
  /** Optional; can be managed in CI instead. */
  version?: string;
  /** Optional; Android version code. */
  versionCode?: number;
  /** Optional; iOS build number. */
  buildNumber?: number;
  splash?: MobileBuildManifestSplash;
  /** 1024×1024 app icon URL. */
  iconUrl?: string;
  /** Splash image URL (e.g. centered logo). */
  splashImageUrl?: string;
  /** Web favicon URL (if you build web). */
  faviconUrl?: string;
}

/**
 * Partial overrides stored in domain setting mobileBuildManifest.
 * All fields optional; merged with defaults derived from style, page, fqdn.
 */
export type MobileBuildManifestOverrides = Partial<
  Omit<MobileBuildManifest, 'android' | 'ios' | 'splash'>
> & {
  android?: Partial<MobileBuildManifestAndroid>;
  ios?: Partial<MobileBuildManifestIos>;
  splash?: Partial<MobileBuildManifestSplash>;
};

export type ComplianceRequirements = Partial<Record<DocumentType, boolean>>;

export interface FireOnboardingSettings {
  email: string;
  autoApprove: boolean;
}

export interface PaytimeOnboardingSettings {
  subPaytimePlanId: number;
  email: string;
}

export type AntifraudSettings = {
  [key in PaymentProvider]: AntifraudProviderSettings;
};

export interface AntifraudProviderSettings {
  enabled: boolean;
  credentials?: {
    publicKey?: string;
    /** Browser device-fingerprint vendor org id (safe for API responses). */
    deviceFingerprintOrgId?: string;
    /** Prefix concatenated with checkout session id for fingerprint session URL (safe for API responses). */
    deviceFingerprintSessionPrefix?: string;
  };
}

export interface AppInitSettings {
  style: DomainStyleSettings;
  page: DomainPageSettings;
  allowNaturalPersonSignup: boolean;
  allowLegalPersonSignup: boolean;
  paymentMethods: PaymentMethod[];
  complianceRequirements: ComplianceRequirements;
  enabledProviders: PaymentProvider[];
}

export type AppSettingKey = keyof AppInitSettings;

export interface RetentionDefinition {
  paymentMethod: PaymentMethod;
  retentionPeriodDays: number;
}

export type ProviderSettingsOverrides = {
  [key in PaymentProvider]?: Partial<PaymentProviderSettings>;
};

export type ProviderCapabilitiesOverrides = Partial<
  Record<PaymentProvider, Partial<PaymentProviderCapabilities>>
>;
export interface PaymentMethodsSettings {
  [PaymentMethod.PIX]: boolean;
  [PaymentMethod.PIX_AUTOMATIC]: boolean;
  [PaymentMethod.CREDIT_CARD]: boolean;
  [PaymentMethod.BANK_SLIP]: boolean;
  [PaymentMethod.BITCOIN]: boolean;
  [PaymentMethod.SLIPIX]: boolean;
}

/** Domain/recipient allowlist keys for free PIX withdrawal key kinds (not API PixKeyType). */
export interface AllowedWithdrawalPixKeyTypesSettings {
  cpf: boolean;
  cnpj: boolean;
  email: boolean;
  phone: boolean;
  random_key: boolean;
}

export const allowedWithdrawalPixKeyTypeOptions = [
  'cpf',
  'cnpj',
  'email',
  'phone',
  'random_key',
] as const satisfies ReadonlyArray<keyof AllowedWithdrawalPixKeyTypesSettings>;
